

import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchLeaves, updateLeaveStatus } from "../redux/actions/leaveActions";
import {
  FiSearch,
  FiDownload,
  FiFilter,
  FiX,
  FiCheck,
  FiXCircle,
  FiCalendar,
  FiUser,
  FiPhone,
  FiMail,
  FiImage,
  FiClock,
  FiChevronLeft,
  FiChevronRight,
  FiEdit3
} from "react-icons/fi";
import Papa from "papaparse";

const Leaves = () => {
  const dispatch = useDispatch();
  const { leaves, loading } = useSelector((state) => state.leave);

  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [photoUrl, setPhotoUrl] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const itemsPerPage = 10;

  useEffect(() => {
    dispatch(fetchLeaves());
  }, [dispatch]);

  // Filter Logic
  const filteredLeaves = leaves.filter((leave) => {
    const term = searchTerm.toLowerCase();
    const matchSearch =
      leave.employee_name?.toLowerCase().includes(term) ||
      leave.employee_phone?.toLowerCase().includes(term) ||
      leave.employee_email?.toLowerCase().includes(term) ||
      leave.reason?.toLowerCase().includes(term);

    const leaveStartDate = new Date(leave.start_date).setHours(0, 0, 0, 0);
    const fromDateValid = fromDate
      ? leaveStartDate >= new Date(fromDate).setHours(0, 0, 0, 0)
      : true;
    const toDateValid = toDate
      ? leaveStartDate <= new Date(toDate).setHours(0, 0, 0, 0)
      : true;

    return matchSearch && fromDateValid && toDateValid;
  });

  const totalPages = Math.ceil(filteredLeaves.length / itemsPerPage);
  const currentItems = filteredLeaves.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // CSV Export
  const handleDownloadCSV = () => {
    const csvData = filteredLeaves.map((l) => ({
      Employee: l.employee_name || "",
      Phone: l.employee_phone || "",
      Email: l.employee_email || "",
      Start_Date: l.start_date || "",
      End_Date: l.end_date || "",
      Reason: l.reason || "",
      Applied_At: l.applied_at || "",
      Leave_Type: l.leave_gov_type || "",
      Comment: l.leaves_status || "",
      Status: l.status || "",
      Photo_URL: l.photo || "",
    }));

    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Leave_Report_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Reset Filters
  const resetFilters = () => {
    setFromDate("");
    setToDate("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  // Reject Submit
  const handleRejectSubmit = () => {
    if (selectedLeave && rejectReason.trim()) {
      dispatch(updateLeaveStatus(selectedLeave.id, "rejected", rejectReason));
      setRejectModalOpen(false);
      setRejectReason("");
      setSelectedLeave(null);
    }
  };

  // Photo Modal
  const openPhotoModal = (url) => {
    setPhotoUrl(url);
    setPhotoModalOpen(true);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { 
        color: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300", 
        label: "Pending" 
      },
      approved: { 
        color: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300", 
        label: "Approved" 
      },
      rejected: { 
        color: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300", 
        label: "Rejected" 
      },
    };

    const config = statusConfig[status] || { 
      color: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300", 
      label: status 
    };
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color} capitalize`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-IN");
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("en-IN", {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400 text-lg">Loading Leave Records...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Leave Management
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Review and manage employee leave requests
          </p>
        </div>
        <div className="flex items-center gap-3 mt-4 lg:mt-0">
          <button
            onClick={handleDownloadCSV}
            className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-xl transition-colors"
          >
            <FiDownload className="text-lg" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          {/* Search Input */}
          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg" />
            <input
              type="text"
              placeholder="Search by name, phone, email, or reason..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-700 border-0 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white dark:focus:bg-gray-600 transition text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
            />
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 px-4 py-3 rounded-xl transition-colors"
          >
            <FiFilter className="text-lg" />
            Filters
            {(fromDate || toDate) && (
              <span className="bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {[fromDate, toDate].filter(Boolean).length}
              </span>
            )}
          </button>

          {/* Clear Filters */}
          {(fromDate || toDate || searchTerm) && (
            <button
              onClick={resetFilters}
              className="flex items-center gap-2 text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 px-4 py-3 rounded-xl transition-colors"
            >
              <FiX className="text-lg" />
              Clear
            </button>
          )}
        </div>

        {/* Expanded Filters */}
        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                From Date
              </label>
              <div className="relative">
                <FiCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-700 border-0 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white dark:focus:bg-gray-600 transition text-gray-900 dark:text-white"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                To Date
              </label>
              <div className="relative">
                <FiCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-700 border-0 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white dark:focus:bg-gray-600 transition text-gray-900 dark:text-white"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Results Count */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-gray-600 dark:text-gray-400">
          Showing {currentItems.length} of {filteredLeaves.length} leave requests
        </p>
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <span>Page {currentPage} of {totalPages}</span>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <th className="py-4 px-6 text-left text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Employee Details
                </th>
                <th className="py-4 px-6 text-left text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    <FiCalendar />
                    Leave Period
                  </div>
                </th>
                <th className="py-4 px-6 text-left text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Reason & Type
                </th>
                <th className="py-4 px-6 text-left text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    <FiClock />
                    Applied At
                  </div>
                </th>
                <th className="py-4 px-6 text-left text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Photo
                </th>
                <th className="py-4 px-6 text-left text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="py-4 px-6 text-left text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Comment
                </th>
                <th className="py-4 px-6 text-left text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredLeaves.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-12 px-6 text-center">
                    <FiCalendar className="text-4xl text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400 text-lg mb-2">No leave requests found</p>
                    <p className="text-gray-400 dark:text-gray-500 text-sm">
                      Try adjusting your search or filters
                    </p>
                  </td>
                </tr>
              ) : (
                currentItems.map((leave) => (
                  <tr key={leave.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                    {/* Employee Details */}
                    <td className="py-4 px-6">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {leave.employee_name}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                          <FiPhone className="text-xs" />
                          {leave.employee_phone}
                        </p>
                        {leave.employee_email && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                            <FiMail className="text-xs" />
                            {leave.employee_email}
                          </p>
                        )}
                      </div>
                    </td>

                    {/* Leave Period */}
                    <td className="py-4 px-6">
                      <div className="text-sm">
                        <p className="text-gray-900 dark:text-white font-medium">
                          {formatDate(leave.start_date)}
                        </p>
                        <p className="text-gray-500 dark:text-gray-400">
                          to {formatDate(leave.end_date)}
                        </p>
                      </div>
                    </td>

                    {/* Reason & Type */}
                    <td className="py-4 px-6">
                      <div>
                        <p className="text-gray-900 dark:text-white text-sm font-medium">
                          {leave.reason}
                        </p>
                        {leave.leave_gov_type && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {leave.leave_gov_type}
                          </p>
                        )}
                      </div>
                    </td>

                    {/* Applied At */}
                    <td className="py-4 px-6 text-sm text-gray-600 dark:text-gray-400">
                      {formatDateTime(leave.applied_at)}
                    </td>

                    {/* Photo */}
                    <td className="py-4 px-6">
                      {leave.photo ? (
                        <button
                          onClick={() => openPhotoModal(leave.photo)}
                          className="flex items-center gap-1 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 text-sm"
                        >
                          <FiImage className="text-sm" />
                          View
                        </button>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>

                    {/* Status */}
                    <td className="py-4 px-6">
                      {getStatusBadge(leave.status)}
                    </td>

                    {/* Comment */}
                    <td className="py-4 px-6 text-sm text-gray-600 dark:text-gray-400 max-w-xs">
                      {leave.leaves_status || "-"}
                    </td>

                    {/* Actions */}
                    <td className="py-4 px-6">
                      {leave.status === "pending" ? (
                        <div className="flex flex-col gap-2">
                          <button
                            onClick={() =>
                              dispatch(updateLeaveStatus(leave.id, "approved", "Approved by Admin"))
                            }
                            className="flex items-center gap-1 bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded-lg transition-colors text-xs"
                          >
                            <FiCheck className="text-xs" />
                            Approve
                          </button>
                          <button
                            onClick={() => {
                              setSelectedLeave(leave);
                              setRejectModalOpen(true);
                            }}
                            className="flex items-center gap-1 bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded-lg transition-colors text-xs"
                          >
                            <FiXCircle className="text-xs" />
                            Reject
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() =>
                            dispatch(updateLeaveStatus(leave.id, "pending", "Status reset by Admin"))
                          }
                          className="flex items-center gap-1 bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded-lg transition-colors text-xs"
                        >
                          <FiEdit3 className="text-xs" />
                          Reset
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="flex items-center gap-2 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiChevronLeft />
            Previous
          </button>

          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <span>Page {currentPage} of {totalPages}</span>
          </div>

          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage >= totalPages}
            className="flex items-center gap-2 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
            <FiChevronRight />
          </button>
        </div>
      )}

      {/* Reject Modal */}
      {rejectModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Reject Leave Request
              </h3>
              <button
                onClick={() => {
                  setRejectModalOpen(false);
                  setRejectReason("");
                  setSelectedLeave(null);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <FiX className="text-xl" />
              </button>
            </div>
            <div className="p-4">
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Please provide a reason for rejecting this leave request:
              </p>
              <textarea
                rows={4}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="w-full bg-gray-50 dark:bg-gray-700 border-0 rounded-xl py-3 px-4 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition placeholder-gray-500 dark:placeholder-gray-400"
                placeholder="Enter rejection reason..."
              />
            </div>
            <div className="flex gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => {
                  setRejectModalOpen(false);
                  setRejectReason("");
                  setSelectedLeave(null);
                }}
                className="flex-1 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectSubmit}
                disabled={!rejectReason.trim()}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Submit Rejection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Photo Modal */}
      {photoModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Leave Document
              </h3>
              <button
                onClick={() => setPhotoModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <FiX className="text-xl" />
              </button>
            </div>
            <div className="p-4">
              <img
                src={photoUrl}
                alt="Leave Document"
                className="max-w-full max-h-[70vh] object-contain rounded-lg"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Leaves;